'use client';

import { memo, useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Wallet, ChevronDown, Bell } from 'lucide-react';
import { formatCurrency } from '@/lib/utils/format';
import { Card } from '@/components/common/Card';
import { AppImage } from '@/components/common/AppImage';
import type { Bid } from '@/lib/types/auction';
import { logger } from '@/lib/monitoring/logger';

interface BidHistoryProps {
  bids: Bid[];
}

export const BidHistory = memo(({ bids }: BidHistoryProps) => {
  const [showAll, setShowAll] = useState(false);
  const visibleBids = showAll ? bids : bids.slice(0, 5); // Show first 5 bids by default

  // Use this ref to track previous bid count for animations
  const prevBidCountRef = useRef(bids.length);
  // Trigger animation when new bids come in
  const [animateNewBid, setAnimateNewBid] = useState(false);
  // Track the newest bid ID for highlighting
  const [newestBidId, setNewestBidId] = useState<string | null>(null);
  // Reference to the animation timeout
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Reference to the nested timeout for clearing newestBidId
  const nestedTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // If we have more bids than before, trigger animation
    if (bids.length > prevBidCountRef.current && bids.length > 0) {
      // Clear any existing animation timeouts
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }

      if (nestedTimeoutRef.current) {
        clearTimeout(nestedTimeoutRef.current);
        nestedTimeoutRef.current = null;
      }

      // Set animation states
      setAnimateNewBid(true);
      setNewestBidId(bids[0]?.id || null);

      // Log for debugging
      logger.debug('BidHistory: New bid detected', {
        prevCount: prevBidCountRef.current,
        newCount: bids.length,
        newestBidId: bids[0]?.id,
      });

      // Reset animation flag after exactly 2 seconds
      animationTimeoutRef.current = setTimeout(() => {
        setAnimateNewBid(false);

        // Log for debugging
        logger.debug('BidHistory: Reset animation state');

        // Clear the newest bid ID after animation completes
        nestedTimeoutRef.current = setTimeout(() => {
          setNewestBidId(null);

          // Log for debugging
          logger.debug('BidHistory: Cleared newest bid ID');
        }, 500);
      }, 2000);
    }

    // Update ref with current count
    prevBidCountRef.current = bids.length;

    // Cleanup timeouts on unmount or when bids change
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
        animationTimeoutRef.current = null;
      }

      if (nestedTimeoutRef.current) {
        clearTimeout(nestedTimeoutRef.current);
        nestedTimeoutRef.current = null;
      }
    };
  }, [bids.length, bids]);

  return (
    <Card className="flex h-full flex-col">
      <div className="flex h-full flex-col p-2">
        {/* 🔹 Title with Bid Count Badge */}
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Bid History</h3>
          {bids.length > 0 && (
            <motion.div
              initial={{ scale: 0.8 }}
              animate={{
                scale: animateNewBid ? [1, 1.2, 1] : 1,
                rotate: animateNewBid ? [0, -5, 5, -5, 0] : 0,
              }}
              transition={{ duration: 0.5 }}
              className="flex items-center space-x-1 rounded-full bg-primary-100 px-2 py-0.5 text-xs font-medium text-primary-800"
            >
              {animateNewBid && (
                <Bell className="mr-1 h-3 w-3 text-primary-600" />
              )}
              <span>
                {bids.length === 1 ? 'Last bid' : `Last ${bids.length} bids`}
              </span>
            </motion.div>
          )}
        </div>

        {/* ✅ Empty State */}
        {bids.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center rounded-lg bg-gray-50 p-6">
            <Wallet className="mb-2 h-10 w-10 text-gray-400" />
            <p className="text-center text-gray-500">No bids yet</p>
            <p className="mt-1 text-center text-xs text-gray-400">
              Bids will appear here as they are placed
            </p>
          </div>
        ) : (
          <div className="flex-1 space-y-4 overflow-auto">
            <AnimatePresence>
              {visibleBids.map((bid) => (
                <motion.div
                  key={bid.id}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{
                    opacity: 1,
                    y: 0,
                    scale:
                      bid.id === newestBidId && animateNewBid
                        ? [1, 1.05, 1]
                        : 1,
                    boxShadow:
                      bid.id === newestBidId && animateNewBid
                        ? [
                            '0 0 0 rgba(59, 130, 246, 0)',
                            '0 0 15px rgba(59, 130, 246, 0.5)',
                            '0 0 0 rgba(59, 130, 246, 0)',
                          ]
                        : 'none',
                  }}
                  exit={{ opacity: 0, y: -10, height: 0 }}
                  transition={{
                    duration: 0.3,
                    scale: { duration: 0.5 },
                    boxShadow: { duration: 2 },
                  }}
                  className={`flex items-center justify-between rounded-lg p-3 transition-all ${
                    bid.id === newestBidId && animateNewBid
                      ? 'border border-primary-500 bg-accent-blue/10 shadow-md'
                      : 'bg-gray-50/80 hover:bg-gray-100/80'
                  }`}
                >
                  {/* 🔹 Team Details */}
                  <div className="flex items-center space-x-3">
                    <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-full border border-gray-300 bg-gray-200">
                      <AppImage
                        src={bid.team?.logo || ''}
                        alt={bid.team?.name || 'Team'}
                        shape="circle"
                        size="sm"
                        gradient
                        className="h-full w-full rounded-full object-cover"
                        fallbackText={bid.team?.name?.[0] || 'T'}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {bid.team?.name || 'Unknown Team'}
                    </span>
                  </div>

                  {/* 🔹 Bid Amount Display */}
                  <div className="flex items-center space-x-1 font-semibold text-gray-900">
                    <Wallet
                      className={`h-5 w-5 ${bid.id === newestBidId && animateNewBid ? 'text-primary-600' : 'text-primary-500'}`}
                    />
                    <span
                      className={`text-lg font-bold ${
                        bid.id === newestBidId && animateNewBid
                          ? 'text-primary-600'
                          : 'text-gray-800'
                      }`}
                    >
                      {formatCurrency(bid.amount)}
                    </span>
                    {bid.id === newestBidId && animateNewBid && (
                      <motion.span
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0 }}
                        className="ml-1 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-bold text-yellow-800"
                      >
                        NEW
                      </motion.span>
                    )}
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* 🔹 View More Button (If More Than 5 Bids) */}
            {bids.length > 5 && (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setShowAll((prev) => !prev)}
                className="mt-3 flex w-full items-center justify-center space-x-2 rounded-lg bg-gray-100 p-2 text-sm font-medium text-gray-700 transition hover:bg-gray-200"
              >
                <span>
                  {showAll ? 'Show Less' : `View All ${bids.length} Bids`}
                </span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${
                    showAll ? 'rotate-180' : ''
                  }`}
                />
              </motion.button>
            )}
          </div>
        )}
      </div>
    </Card>
  );
});

BidHistory.displayName = 'BidHistory';
